import type { VariantProps } from "class-variance-authority";
import { cva } from "class-variance-authority";
import { forwardRef } from "react";

import { cn } from "../../../lib";

const cardWithTitleVariants = cva(
  "overflow-hidden divide-y divide-gray-200 bg-white shadow sm:rounded-lg border border-gray-200",
  {
    variants: {
      variant: {
        default: "",
      },
      size: {
        default: "",
        compact: "",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

const cardHeaderVariants = cva("text-lg font-extrabold bg-gray-100", {
  variants: {
    variant: {
      default: "",
    },
    size: {
      default: "px-4 py-5 sm:px-6",
      compact: "px-2 py-2.5 sm:px-3",
    },
  },
  defaultVariants: {
    variant: "default",
    size: "default",
  },
});

const cardContentVariants = cva("", {
  variants: {
    variant: {
      default: "",
    },
    size: {
      default: "px-4 py-5 sm:p-6",
      compact: "px-2 py-2.5 sm:p-3",
    },
  },
  defaultVariants: {
    variant: "default",
    size: "default",
  },
});

interface CardWithTitleProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardWithTitleVariants>,
    VariantProps<typeof cardHeaderVariants>,
    VariantProps<typeof cardContentVariants> {
  header: string | JSX.Element;
}

const CardWithTitle = forwardRef<HTMLDivElement, CardWithTitleProps>(
  (props, ref) => {
    const { variant = "default", size = "default", className, ...rest } = props;
    return (
      <div
        className={cn(cardWithTitleVariants({ variant, size }), className)}
        {...rest}
        ref={ref}
      >
        <div className={cn(cardHeaderVariants({ variant, size }))}>
          {/* Content goes here */}
          {/* We use less vertical padding on card headers on desktop than on body sections */}
          {props.header}
        </div>
        <div className={cn(cardContentVariants({ variant, size }))}>
          {props.children}
        </div>
      </div>
    );
  },
);

CardWithTitle.displayName = "Card";

export default CardWithTitle;
export { cardWithTitleVariants };
export type { CardWithTitleProps };
